import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { useAuth } from "../../contexts/AuthContext";
import Loading from "../../components/Loading";
import AddNewButton from "../../components/buttons/AddNewButton";
import SaveButton from "../../components/buttons/SaveButton";
import CancelButton from "../../components/buttons/CancelButton";
import { partService } from "../../services/api";
import PartForm from "./PartForm";

import styles from "./PartsTable.module.css";

function buildExportFileName() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `parts-export-${y}-${m}-${day}.xlsx`;
}

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function emptyPartDraft() {
  return {
    partNumber: "",
    description: "",
    category: "",
    notes: "",
  };
}

function PartsTable() {
  const { hasPermission } = useAuth();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [partNumber, setPartNumber] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");

  const dq = useDebouncedValue(partNumber, 350);
  const dd = useDebouncedValue(description, 350);
  const dc = useDebouncedValue(category, 350);

  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState(emptyPartDraft);
  const [modalError, setModalError] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchParts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (dq.trim()) {
        params.partNumber = dq.trim();
      }
      if (dd.trim()) {
        params.description = dd.trim();
      }
      if (dc.trim()) {
        params.category = dc.trim();
      }
      const res = await partService.getAll(params);
      setParts(res.data.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load parts");
      setParts([]);
    } finally {
      setLoading(false);
    }
  }, [dq, dd, dc]);

  useEffect(() => {
    fetchParts();
  }, [fetchParts]);

  const handleExport = () => {
    const rows = parts.map((p) => ({
      "Part number": p.partNumber ?? "",
      Description: p.description ?? "",
      Category: p.category ?? "",
      Notes: p.notes ?? "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Parts");
    XLSX.writeFile(workbook, buildExportFileName());
  };

  const openModal = () => {
    setDraft(emptyPartDraft());
    setModalError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalError(null);
    setDraft(emptyPartDraft());
  };

  const handleFieldChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveNew = async () => {
    setModalError(null);
    setSaving(true);
    try {
      await partService.create({
        partNumber: draft.partNumber,
        description: draft.description,
        category: draft.category,
        notes: draft.notes || null,
      });
      closeModal();
      await fetchParts();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 409 ? "Part number already exists" : "Failed to save part");
      setModalError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading && parts.length === 0 && !error) {
    return (
      <div className={styles.tableWrap}>
        <Loading loadingText="Loading parts..." />
      </div>
    );
  }

  return (
    <>
      <div className={styles.toolbar}>
        <div className={styles.filtersRow}>
          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="parts-filter-pn">
              Part number
            </label>
            <input
              id="parts-filter-pn"
              className={styles.filterInput}
              value={partNumber}
              onChange={(e) => setPartNumber(e.target.value)}
              placeholder="Contains…"
              autoComplete="off"
            />
          </div>
          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="parts-filter-desc">
              Description
            </label>
            <input
              id="parts-filter-desc"
              className={styles.filterInput}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Contains…"
              autoComplete="off"
            />
          </div>
          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="parts-filter-cat">
              Category
            </label>
            <input
              id="parts-filter-cat"
              className={styles.filterInput}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Contains…"
              autoComplete="off"
            />
          </div>
        </div>
        <div className={styles.actionsRow}>
          {hasPermission("component_orders:create") && (
            <AddNewButton text="+ Add part" onClick={openModal} />
          )}
          {hasPermission("component_orders:export") && (
            <button
              type="button"
              className={styles.exportButton}
              onClick={handleExport}
              disabled={parts.length === 0}
            >
              Export to .xlsx
            </button>
          )}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {parts.length === 0 && !loading ? (
        <p className={styles.empty}>No parts match the current filters.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Part number</th>
                <th>Description</th>
                <th>Category</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => (
                <tr key={p.id}>
                  <td>
                    <Link className={styles.rowLink} to={`/parts/${p.id}`}>
                      {p.partNumber}
                    </Link>
                  </td>
                  <td>{p.description || "—"}</td>
                  <td>{p.category || "—"}</td>
                  <td className={styles.notesCell} title={p.notes || ""}>
                    {p.notes || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div className={styles.modalOverlay} role="presentation" onClick={closeModal}>
          <div
            className={styles.modalPanel}
            role="dialog"
            aria-labelledby="parts-add-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2 id="parts-add-title" className={styles.modalTitle}>
                Add part
              </h2>
              <button type="button" className={styles.modalClose} onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {modalError && <div className={styles.modalError}>{modalError}</div>}
              <PartForm values={draft} onFieldChange={handleFieldChange} idPrefix="parts-add" />
              <div className={styles.modalActions}>
                <SaveButton text={saving ? "Saving…" : "Save"} onClick={handleSaveNew} disabled={saving} />
                <CancelButton onClick={closeModal} disabled={saving} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PartsTable;
